import { useState } from 'react'
import { GridRowParams } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { type GridColDef, Pagination } from '@dhl/components'
import { Body1, Box, Button, Card, IconButton } from '@dhl-ui/components'
import { ConfirmationDialog } from '@dhl/confirmation-dialog'
import { UnlinkIcon } from '@dhl-ui/icons'
import {
  useDeleteOrganisationAccountManager,
  useGetOrganisationsByAcccountManager,
  useGetOrganisationsHook,
  useAddOrganisationAccountManagersHook,
  type OrganisationDto,
} from '@dhl/mdp-api'

import type { User } from '~/modules/users/model'
import {
  AdminDataGrid,
  useNotificationMessage,
  SelectableDataTableDialog,
  type GetDataFn,
  type UpdateDataFn,
} from '~/common'

type Props = {
  user: User
}
export const UsersAccountManagerCard = ({ user: { id: userId } }: Props) => {
  const { t } = useTranslation()
  const [accountManagerDialogOpen, setAccountManagerDialogOpen] = useState<boolean>(false)
  const [page, setPage] = useState<number>(0)
  const [perPage, setPerPage] = useState<number>(10)
  const navigate = useNavigate()
  const [selectedOrganization, setSelectedOrganization] = useState<OrganisationDto | undefined>(undefined)
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState<boolean>(false)
  const { showError, showSuccess } = useNotificationMessage()
  const getOrganisations = useGetOrganisationsHook()
  const addOrganisationAccountManagers = useAddOrganisationAccountManagersHook()

  const { data, refetch: refetchOrganisations } = useGetOrganisationsByAcccountManager(userId, { skip: page * perPage })
  const { mutateAsync: deleteAccountManager } = useDeleteOrganisationAccountManager()

  const unlinkOrganisation = () => {
    if (selectedOrganization) {
      deleteAccountManager({
        id: selectedOrganization.id,
        userId,
      })
        .then(() => {
          showSuccess(t('admin.accountManagers.organisationUnlinked'))
          refetchOrganisations().then(() => {})
        })
        .catch(() => showError(t('admin.accountManagers.organisationUnlinkFailed')))
        .finally(() => {
          setShowUnlinkConfirm(false)
        })
    }
  }

  const columns: GridColDef<OrganisationDto>[] = [
    {
      field: 'name',
      headerName: t('admin.accountManagers.name'),
      sortable: true,
      width: 120,
      renderCell: ({ row: organisation }) => <Body1>{organisation?.name ?? ''}</Body1>,
    },
    {
      field: 'businessUnitCode',
      headerName: t('admin.accountManagers.businessUnitCode'),
      sortable: true,
      flex: 1,
      renderCell: ({ row: organisation }) => <Body1>{organisation?.businessUnitCode ?? ''}</Body1>,
    },
    {
      field: 'actions',
      headerName: t('admin.accountManagers.actions'),
      renderCell: ({ row: organisation }) => (
        <IconButton
          aria-label="unlink organisation"
          onClick={() => {
            setSelectedOrganization(organisation)
            setShowUnlinkConfirm(true)
          }}
        >
          <UnlinkIcon />
        </IconButton>
      ),
    },
  ]

  const fetchData: GetDataFn<OrganisationDto> = async (page = 0, perPage = 0, search = '') => {
    const { totalCount, content = [] } = await getOrganisations({
      query: search,
      skip: page * perPage,
      max: perPage,
    })
    return [totalCount, content]
  }
  const handleUpdate: UpdateDataFn = (organisationIds: string[]) => {
    Promise.all(
      organisationIds.map((organisationId) =>
        addOrganisationAccountManagers(organisationId, { accountManagers: [userId] })
      )
    )
      .then(() => {
        showSuccess(t('admin.accountManagers.accountManagerLinked'))
        refetchOrganisations().then(() => {})
      })
      .catch(() => showError(t('admin.accountManagers.accountManagerLinkFailed')))
  }

  const handleRowClick = ({ id }: GridRowParams<OrganisationDto>) => {
    navigate(`/organisations/details/${id}`)
  }

  return (
    <>
      <Card>
        <Card.Header
          title={t('admin.accountManagers.title.users')}
          endNode={
            <Button variant="contained" onClick={() => setAccountManagerDialogOpen(true)}>
              {t('admin.accountManagers.addAccountManager.users')}
            </Button>
          }
        />
        <Card.Content>
          <AdminDataGrid columns={columns} rows={data?.content ?? []} hideFooter onRowClick={handleRowClick} />
          <Box px={2} py={1}>
            <Pagination
              page={page}
              count={data?.totalCount || 0}
              rowsPerPage={perPage}
              onPageChange={(_, newPage) => {
                setPage(newPage)
              }}
              onRowsPerPageChange={setPerPage}
            />
          </Box>
        </Card.Content>
      </Card>
      {showUnlinkConfirm && (
        <ConfirmationDialog
          open={true}
          titleText={t('admin.accountManagers.dangerZone')}
          acceptText={t('admin.accountManagers.unlinkOrganisation')}
          rejectText={t('admin.accountManagers.cancel')}
          onAccept={unlinkOrganisation}
          onReject={() => setShowUnlinkConfirm(false)}
          onClose={() => setShowUnlinkConfirm(false)}
        />
      )}
      {accountManagerDialogOpen && (
        <SelectableDataTableDialog<OrganisationDto>
          title={t('admin.accountManagers.addAccountManager.users')}
          open={accountManagerDialogOpen}
          columns={[
            {
              field: 'name',
              headerName: t('admin.accountManagers.name'),
              flex: 1,
              renderCell: ({ row }) => <Body1>{row?.name ?? ''}</Body1>,
            },
          ]}
          getData={fetchData}
          onUpdate={handleUpdate}
          onClose={() => setAccountManagerDialogOpen(false)}
          disabledRowIds={data?.content?.map(({ id }) => id) ?? []}
        />
      )}
    </>
  )
}
